import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  ChartOptions,
} from "chart.js";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";

import { chartGridLineCol, darkGreyCol, midGreyCol } from "../colors";
import { createClickableXAxisPlugin } from "../plugins/clickableXAxisPlugin";
import { createCustomLabelBackgroundPlugin } from "../plugins/customLabelBackgroundPlugin";
import { createHoverLinePlugin } from "../plugins/hoverLinePlugin";
import { externalTooltipHandler } from "../utils/TooltipUtils";

// Register required Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip
);

interface Dataset {
  label?: string;
  data: number[];
  borderColor: string;
  backgroundColor: string;
  yAxisID: string;
  yAxisLabel: string;
  pointRadius?: number;
  pointBackgroundColor?: string;
  tension?: number;
  pointBorderColor?: string;
  pointBorderWidth?: number;
  pointHoverBorderColor?: string;
  pointHoverRadius?: number;
  hoverBackgroundColor?: string;
  hoverBorderColor?: string;
}

interface MultiAxisChartProps {
  data: {
    labels: string[];
    datasets: Dataset[];
  };
  height?: number;
  maintainAspectRatio?: boolean;
  customTooltip?: ((tooltip: { dataPoints: any[] }) => React.ReactNode) | null;
  xAxisMin: number;
  xAxisMax?: number;
  handleSelectedLabelIndex: (
    selectedLabelIndex: number | null,
    selectedLabelIndexPos: { x: number | null; y: number | null }
  ) => void;
}

const MultiAxisChart = ({
  data,
  height = 800,
  maintainAspectRatio = false,
  customTooltip = null,
  xAxisMin,
  xAxisMax,
  handleSelectedLabelIndex,
}: MultiAxisChartProps) => {
  const [selectedLabelIndex, setSelectedLabelIndex] = useState<number | null>(
    null
  );
  const [selectedLabelIndexPos, setSelectedLabelIndexPos] = useState<{
    x: number | null;
    y: number | null;
  }>({
    x: null,
    y: null,
  });

  const selectedLabelIndexRef = useRef<number | null>(null); // New ref to store the state
  const chartRef = useRef<ChartJS<"line"> | null>(null);
  const isY1Available = useMemo(
    () => data.datasets.length > 1,
    [data.datasets.length]
  );

  useEffect(() => {
    selectedLabelIndexRef.current = selectedLabelIndex;
    handleSelectedLabelIndex(selectedLabelIndex, {
      x: selectedLabelIndexPos.x,
      y: selectedLabelIndexPos.y,
    });
  }, [
    selectedLabelIndex,
    handleSelectedLabelIndex,
    selectedLabelIndexPos?.x,
    selectedLabelIndexPos?.y,
  ]);

  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio,
    layout: {
      padding: {
        right: isY1Available ? 0 : 72, // Reserve enough space for right y-axis even if hidden
        bottom: -10,
      },
    },
    hover: {
      mode: "dataset",
      intersect: false,
    },
    plugins: {
      stripedBackground: true,
      legend: { display: false },
      datalabels: { display: false },
      ...(customTooltip !== null
        ? {
            tooltip: {
              enabled: false,
              external: (context: { tooltip: any; chart: any }) => {
                const dataset = context?.tooltip?.dataPoints?.[0]
                  ?.dataset as any;

                if (!dataset || dataset?.temp) return;

                if (!context.tooltip || !context.chart) return;
                const viewportWidth = window.innerWidth;

                const tooltipX = context?.tooltip?.caretX ?? 0;

                // Estimate tooltip width (you can refine this)
                const estimatedTooltipWidth = 600;

                // Check if tooltip would overflow off the screen
                const willOverflow =
                  tooltipX + estimatedTooltipWidth > viewportWidth;

                return externalTooltipHandler(
                  context,
                  !willOverflow,
                  customTooltip as any
                );
              },
            },
          }
        : {}),
    } as ChartOptions<"line">["plugins"],
    scales: {
      x: {
        border: {
          display: false,
        },
        grid: { display: false, drawTicks: false, drawOnChartArea: false },
        offset: true,
        ticks: {
          color: "transparent",
          backdropColor: "transparent",
          padding: 14,
          // drawOnChartArea: false,
        },
        // min: 2, // Start from index 2
      },
      y: {
        border: {
          display: false,
        },
        type: "linear",
        position: "left",
        title: {
          display: true,
          text: data.datasets[0]?.yAxisLabel,
          color: darkGreyCol,
          font: {
            size: 12,
            weight: 500,
          },
        },
        grid: {
          drawTicks: false,
          color: chartGridLineCol,
          lineWidth: 1,
        },
        ticks: {
          padding: 6,
          color: midGreyCol,
          maxTicksLimit: 6,
          font: {
            size: 13,
            weight: 400,
          },
        },
      },
      ...(isY1Available
        ? {
            y1: {
              border: {
                display: false,
              },
              type: "linear",
              position: "right",
              title: {
                display: true,
                text: data.datasets[1]?.yAxisLabel,
                color: darkGreyCol,
                font: {
                  size: 12,
                  weight: 500,
                },
              },
              grid: {
                drawOnChartArea: false,
                drawTicks: false,
                color: chartGridLineCol,
              },
              ticks: {
                padding: 8,
                color: midGreyCol,
                maxTicksLimit: 6,
                font: {
                  size: 13,
                  weight: 400,
                },
              },
            },
          }
        : {}),
    },
  };

  const customYAxisLegend = {
    id: "customYAxisLegend",
    beforeDraw: (chart: any) => {
      const { ctx, scales } = chart;
      const yAxis = scales.y;
      const y1Axis = scales.y1;

      ctx.save();

      // Function to draw a circle next to the axis label
      const drawCircle = (x: number, y: number, color: string) => {
        ctx.beginPath();
        ctx.arc(x, y, 6, 0, 2 * Math.PI);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.closePath();
      };

      const yLabel = yAxis.options.title?.text || "";
      const textWidth = ctx.measureText(yLabel).width;

      // Draw circle for Y-axis (GHG Emissions)
      if (yAxis) {
        const yLabelX = yAxis.left + 12; // Position slightly left of Y-axis label
        const yLabelY =
          yAxis.top + (yAxis.bottom - yAxis.top) / 2 + textWidth / 2 + 5; // Center vertically
        drawCircle(yLabelX, yLabelY, "#009688");
      }

      // Draw circle for Y1-axis (Sales)
      const y1Label = y1Axis?.options?.title?.text || "";
      const textWidth1 = ctx.measureText(y1Label).width;
      if (y1Axis) {
        const y1LabelX = y1Axis.right - 12.5; // Position slightly right of Y1-axis label
        const y1LabelY =
          yAxis.top + (yAxis.bottom - yAxis.top) / 2 - textWidth1 / 2 - 5; // Center vertically

        drawCircle(y1LabelX, y1LabelY, "#CDDC39");
      }

      ctx.restore();
    },
  };

  const customLabelBackgroundPlugin = createCustomLabelBackgroundPlugin<"line">(
    {
      selectedLabelIndexRef,
      backgroundColor: darkGreyCol,
    }
  );

  const clickableXAxisPlugin2 = createClickableXAxisPlugin<"line">({
    id: "clickableXAxis",
    selectedLabelIndexRef,
    setSelectedLabelIndex,
    setSelectedLabelIndexPos,
    dataLabels: data.labels,
    xAxisMin,
    xAxisMax,
  });

  const hoverLinePlugin = createHoverLinePlugin<"line">({
    color: midGreyCol,
    lineWidth: 1,
    selectedLabelIndexRef,
  });

  return (
    <div style={{ width: "100%", height }}>
      <Line
        data={data}
        ref={chartRef}
        options={options}
        plugins={[
          customYAxisLegend,
          customLabelBackgroundPlugin,
          clickableXAxisPlugin2,
          hoverLinePlugin,
        ]}
      />
    </div>
  );
};

export default MultiAxisChart;
